import { useEffect, useState } from 'react';

export const IMAGE_FILE_ACCEPT = 'image/*';

export function isImageFile(file: File | undefined): file is File {
  return !!file && file.type.startsWith('image/');
}

export async function blobToImageData(blob: Blob): Promise<ImageData> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('OffscreenCanvas 2d context unavailable');
    ctx.drawImage(bitmap, 0, 0);
    return ctx.getImageData(0, 0, bitmap.width, bitmap.height);
  } finally {
    bitmap.close();
  }
}

/**
 * Convert ImageData → ImageBitmap with proper cleanup. The previous bitmap is
 * closed one frame after replacement so any in-flight Konva draw cycle finishes
 * with it intact (otherwise: "drawImage on detached image source"). On unmount
 * the final bitmap is closed to release GPU memory.
 */
export function useImageBitmap(imageData: ImageData | null): ImageBitmap | null {
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);

  useEffect(() => {
    if (!imageData) {
      setBitmap(null);
      return;
    }
    let cancelled = false;
    void createImageBitmap(imageData).then((bm) => {
      if (cancelled) bm.close();
      else setBitmap(bm);
    });
    return () => {
      cancelled = true;
    };
  }, [imageData]);

  useEffect(() => {
    if (!bitmap) return;
    return () => {
      const stale = bitmap;
      requestAnimationFrame(() => stale.close());
    };
  }, [bitmap]);

  return bitmap;
}
